import React, { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';

export const ProtectedRoute = ({ children }) => {
  const { user, loading, initialized, authError, sessionExpired, login, checkRedirectGuard, setRedirectGuard } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Show loading spinner ONLY while loading is true
  if (loading) {
    console.log('[ProtectedRoute] Loading state, showing spinner');
    return (
      <div className="min-h-screen bg-ss-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-ss-cyan border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-ss-muted">Loading...</p>
        </div>
      </div>
    );
  }

  // Show session expired screen (stable UI, no auto-redirect loop)
  // User must click button to login again
  if (sessionExpired || (authError && !user)) {
    console.log('[ProtectedRoute] Session expired or auth error - showing recovery screen');
    
    // Check if we've already redirected once this session (Safari loop prevention)
    const alreadyRedirected = checkRedirectGuard();
    
    return (
      <div className="min-h-screen bg-ss-bg flex items-center justify-center">
        <div className="text-center max-w-md mx-4">
          <div className="w-16 h-16 rounded-full bg-ss-surface flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-ss-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-ss-text mb-2">Session Expired</h2>
          <p className="text-ss-muted mb-6">
            {authError || 'Your session has expired. Please sign in again to continue.'}
          </p>
          <Button
            onClick={() => {
              setRedirectGuard(); // Prevent loop if login fails
              login();
            }}
            data-testid="signin-again-btn"
            className="bg-ss-cyan hover:bg-ss-cyan/90 text-black font-semibold px-6 py-2"
          >
            Sign in again
          </Button>
          {alreadyRedirected && (
            <p className="text-xs text-ss-muted mt-4">
              If you're having trouble signing in, try clearing your browser cookies.
            </p>
          )}
        </div>
      </div>
    );
  }

  // Only redirect to login AFTER loading is complete and user is definitively null
  // Use redirect guard to prevent Safari loop
  if (!user) {
    const alreadyRedirected = checkRedirectGuard();
    
    if (alreadyRedirected) {
      console.log('[ProtectedRoute] Already redirected once, showing session expired screen');
      return (
        <div className="min-h-screen bg-ss-bg flex items-center justify-center">
          <div className="text-center max-w-md mx-4">
            <h2 className="text-xl font-semibold text-ss-text mb-2">Sign In Required</h2>
            <p className="text-ss-muted mb-6">Please sign in to access this page.</p>
            <Button
              onClick={login}
              data-testid="signin-btn"
              className="bg-ss-cyan hover:bg-ss-cyan/90 text-black font-semibold px-6 py-2"
            >
              Sign in with Google
            </Button>
          </div>
        </div>
      );
    }
    
    console.log('[ProtectedRoute] No user after loading complete, redirecting to login from:', location.pathname);
    setRedirectGuard();
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  console.log('[ProtectedRoute] User authenticated, rendering:', location.pathname);
  return children;
};

export default ProtectedRoute;
